import React, { useEffect, useContext } from "react";
import { LoadingContext } from './../../../src/Pages/Utility/spinner/LoadingContext';
import UsePanel from "./UserPanel";
import CategoryChartCard from "./ProjectCategoryChartCard";
import OverView from "./OverView";
import LatestValuations from "./LatestValuations";
import ProjectStatus from "./ProjectStatus";
import Reviewers from "./Reviewers";
import Capturing from "./Capturing";
import BarcodeGenerationStats from "./BarcodeGenerationStats";
import { Badge, Col, Container, Row } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import "./Dashboard.css";

const Dashboard = () => {
  const { setIsLoading } = useContext(LoadingContext);
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
  const fullNameFromParts = [authUser?.user?.first_name, authUser?.user?.last_name]
    .filter(Boolean)
    .join(" ");
  const currentUserName =
    fullNameFromParts ||
    authUser?.user?.full_name ||
    authUser?.user?.name ||
    authUser?.user?.display_name ||
    authUser?.user?.username ||
    "Team";

  useEffect(() => {
    document.title = "Dashboard | Valitan";

    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
      setIsLoading(false);
    };
  }, [setIsLoading]);

  return (
    <div className="valitan-dashboard">
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Dashboard" />

          <section className="dashboard-hero mb-4">
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <div className="hero-kicker">
                  <span className="rounded-circle bg-warning" style={{ width: 8, height: 8 }} />
                  Valitan Operations Workspace
                </div>
                <h1 className="hero-title mb-3">Welcome back, {currentUserName}.</h1>
                <p className="hero-copy mb-0">
                  Monitor valuation flow, project distribution, and review workload from one modern
                  control surface built for structured delivery.
                </p>
              </Col>
              <Col lg={5}>
                <div className="hero-meta">
                  <div className="hero-meta-card">
                    <span>Focus</span>
                    <strong>Collection to reporting visibility</strong>
                  </div>
                  <div className="hero-meta-card">
                    <span>Mode</span>
                    <strong>Secure operational dashboard</strong>
                  </div>
                  <div className="hero-meta-card">
                    <span>Status</span>
                    <strong>Live overview across valuation activity</strong>
                  </div>
                </div>
              </Col>
            </Row>
          </section>

          <section className="mb-4">
            <div className="mb-3">
              <h2 className="dashboard-section-title mb-1">Performance Snapshot</h2>
              <p className="dashboard-section-copy">
                Key activity indicators for the current operating cycle.
              </p>
            </div>
            <UsePanel />
          </section>

          <section className="mb-4">
            <div className="mb-3">
              <h2 className="dashboard-section-title mb-1">Progress Tracking</h2>
              <p className="dashboard-section-copy">
                Current stage movement across the asset valuation workflow.
              </p>
            </div>
            <div className="dashboard-shell-card p-4">
              <div className="progress-tracker">
                {[
                  {
                    title: "Data Collection and Verification",
                    status: "Done",
                    stateClass: "is-done",
                  },
                  {
                    title: "Data Review",
                    status: "In Progress",
                    stateClass: "is-active",
                  },
                  {
                    title: "Barcode Generation",
                    status: "In Progress",
                    stateClass: "is-active",
                  },
                  {
                    title: "Data Analysis",
                    status: "Not Yet",
                    stateClass: "is-upcoming",
                  },
                  {
                    title: "Reporting",
                    status: "Not Yet",
                    stateClass: "is-upcoming",
                  },
                ].map((step, index) => (
                  <div key={step.title} className={`progress-step ${step.stateClass}`}>
                    <div className="progress-step-top">
                      <span className="progress-marker">
                        {step.stateClass === "is-done" ? <i className="ri-check-line" /> : index + 1}
                      </span>
                      <span className="progress-line" />
                    </div>
                    <div className="progress-content">
                      <h6>{step.title}</h6>
                      <span>{step.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-4">
            <div className="mb-3">
              <h2 className="dashboard-section-title mb-1">Pipeline Intelligence</h2>
              <p className="dashboard-section-copy">
                Current valuation trends, category mix, and workload signals.
              </p>
            </div>
            <Row className="g-4">
              <OverView />
              <CategoryChartCard />
            </Row>
          </section>

          <BarcodeGenerationStats />

          <section className="mb-4">
            <div className="mb-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div>
                <h2 className="dashboard-section-title mb-1">Recent Activity</h2>
                <p className="dashboard-section-copy">
                  Latest valuation records moving through the system.
                </p>
              </div>
              <Badge color="light" className="widget-chip">
                Live operational feed
              </Badge>
            </div>
            <LatestValuations />
          </section>

          <section>
            <div className="mb-3">
              <h2 className="dashboard-section-title mb-1">Operational Detail</h2>
              <p className="dashboard-section-copy">
                Project status distribution and latest alerts in one view.
              </p>
            </div>
            <Row className="g-4">
              <ProjectStatus />
              <Capturing />
              <Reviewers />
            </Row>
          </section>
        </Container>
      </div>
    </div>
  );
};

export default Dashboard;
